import type { ClientPathsWithMethod } from "openapi-fetch";
import parseLinkHeader from "parse-link-header";
import { voislogApi } from "./apiClients/voislogApi";
import type { VoislogPaths } from "./apiClients/voislogApi";

const DEFAULT_PAGE_SIZE = 1000;

// voislogApi.GET が受け付ける全URLパスのユニオン
type ApiPaths = ClientPathsWithMethod<typeof voislogApi, "get">;

// ページネーション可能なエンドポイントのGETスペック条件:
// query に skip/limit を持ち、200レスポンスが配列であること
type PaginatedSpec = {
  parameters: {
    query?: { skip?: number; limit?: number } & Record<string, unknown>;
  };
  responses: { 200: { content: { "application/json": unknown[] } } };
};

// ApiPaths のうち PaginatedSpec を満たすpathだけを抽出したユニオン
type PaginatedPaths = {
  [P in ApiPaths]: VoislogPaths[P] extends { get: PaginatedSpec } ? P : never;
}[ApiPaths];

// path P の get オペレーションのスペックを取り出す
type GetSpec<P extends PaginatedPaths> = VoislogPaths[P] extends {
  get: infer G;
}
  ? G
  : never;

// path P の get の query パラメータ型
type GetQuery<P extends PaginatedPaths> =
  GetSpec<P> extends {
    parameters: { query?: infer Q };
  }
    ? Q
    : never;

// path P の get の path パラメータ型（path パラメータを持たないなら undefined）
type GetPath<P extends PaginatedPaths> =
  GetSpec<P> extends {
    parameters: { path: infer T };
  }
    ? T
    : undefined;

// path P の get の 200レスポンス配列の要素型
type GetItem<P extends PaginatedPaths> =
  GetSpec<P> extends {
    responses: { 200: { content: { "application/json": (infer Item)[] } } };
  }
    ? Item
    : never;

// fetchAllPaginated の第2引数 init の型。
// path パラメータを持つエンドポイントでは path を必須にし、
// 持たないエンドポイントでは path を渡せないようにする
type Init<P extends PaginatedPaths> = {
  params: { query?: GetQuery<P> } & (GetPath<P> extends undefined
    ? { path?: never }
    : { path: GetPath<P> });
};

// fetchAllPaginated の第3引数 options の型
type FetchAllPaginatedOptions = {
  pageSize: number;
};

export async function fetchAllPaginated<P extends PaginatedPaths>(
  path: P,
  init: Init<P>,
  options: FetchAllPaginatedOptions = { pageSize: DEFAULT_PAGE_SIZE },
): Promise<GetItem<P>[]> {
  const collected: GetItem<P>[] = [];
  const { pageSize } = options;
  let skip = 0;
  let limit = pageSize;
  let hasMore = true;

  // voislogApi.GET の init 型 (MaybeOptionalInit<...>) はジェネリック関数内で
  // 自前で組んだ構造と互換にならないため、緩い関数型にキャストして呼ぶ
  const get = voislogApi.GET as unknown as (
    url: string,
    init: { params: { query?: unknown; path?: unknown } },
  ) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;

  while (hasMore) {
    const baseQuery = (init.params.query ?? {}) as Record<string, unknown>;
    const initWithPaging = {
      ...init,
      params: {
        ...init.params,
        query: { ...baseQuery, skip, limit },
      },
    };

    const result = await get(path, initWithPaging);

    // 上のフィルタで data は unknown[] と保証済。openapi-fetch の戻り値型は
    // parseAs などの分岐で配列に解決しないため unknown 経由でキャストする。
    const data = result.data as unknown as GetItem<P>[];
    collected.push(...data);

    const links = parseLinkHeader(result.response.headers.get("Link"));
    const next = links?.next;

    if (!next) {
      hasMore = false;
    } else {
      skip = next.skip !== undefined ? Number(next.skip) : skip + limit;
      limit = next.limit !== undefined ? Number(next.limit) : limit;
    }
  }

  return collected;
}
